import type { CSSProperties, ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'
import type { StageCategory } from '@/shared/types/domain'

export type StatusTone =
  | 'paused'
  | 'not_started'
  | 'in_development'
  | 'done'
  | 'feedback'
  | 'ongoing'
  | 'overdue'
  | 'neutral'

const TONE_VAR: Record<StatusTone, string> = {
  paused: '--phase-paused',
  not_started: '--phase-not-started',
  in_development: '--phase-in-development',
  done: '--phase-done',
  feedback: '--phase-feedback',
  ongoing: '--phase-ongoing',
  overdue: '--overdue',
  neutral: '--text-3',
}

/** Todo estado se comunica con color + icono + texto, nunca solo color. */
const TONE_GLYPH: Record<StatusTone, string> = {
  paused: '‖',
  not_started: '○',
  in_development: '▸',
  done: '✓',
  feedback: '↺',
  ongoing: '∞',
  overdue: '!',
  neutral: '·',
}

/** La categoria del stage manda; el label lo renombra cada empresa. */
export const CATEGORY_TONE: Record<StageCategory, StatusTone> = {
  backlog: 'not_started',
  active: 'in_development',
  review: 'feedback',
  blocked: 'overdue',
  paused: 'paused',
  done: 'done',
}

export interface StatusPillProps {
  children: ReactNode
  tone?: StatusTone
  /** Deriva el tono de la categoria del stage, que es lo que hace el resto del sistema. */
  category?: StageCategory
  size?: 'sm' | 'md'
  /**
   * Cuánto pesa visualmente. **`solid` es la excepción, no el default mental.**
   *
   * Una fila del portafolio declara tres estados a la vez —fase, salud y si es
   * permanente— y con los tres en caja se dibujaban tres rectángulos de color
   * por fila, 22 sólo en la tabla. Ninguno ganaba, y el nombre del proyecto,
   * que es el dato que la gente busca, pesaba menos que cualquiera de ellos.
   *
   * `quiet` conserva los tres canales que pide la dirección de diseño —color,
   * ícono y texto— y suelta lo único que no comunicaba nada: la caja. Se usa
   * para el estado secundario. `solid` queda para la **fase**, que es la
   * dimensión que estructura el tablero y ordena el resto de la app.
   *
   * El contraste no se debilita: `contraste.test.ts` mide los tonos sobre el
   * fondo del chip (el mismo hue al 12%), y sobre la superficie pelada de
   * `quiet` el ratio sólo puede ser mejor. El test lo afirma para las dos.
   */
  variant?: 'solid' | 'quiet'
  /** Version de un click para editar la fase (plan 3.3). */
  onClick?: () => void
  /**
   * Un nodo al final, FUERA del texto: un chevron, un contador.
   *
   * Existe porque `children` se dibuja dentro de un `<span class="truncate">`,
   * que es una caja de texto: un `<svg>` metido ahí se va a su propia línea —el
   * reset les pone `display: block`— y el pill termina de dos renglones. La
   * ranura lo deja como hermano del texto, que es donde tiene que estar.
   */
  trailing?: ReactNode
  className?: string
}

export function StatusPill({
  children,
  tone,
  category,
  size = 'sm',
  variant = 'solid',
  onClick,
  trailing,
  className,
}: StatusPillProps) {
  const resolved: StatusTone = tone ?? (category ? CATEGORY_TONE[category] : 'neutral')
  const token = TONE_VAR[resolved]

  const style: CSSProperties =
    variant === 'quiet'
      ? { color: `hsl(var(${token}))` }
      : {
          color: `hsl(var(${token}))`,
          backgroundColor: `hsl(var(${token}) / .12)`,
          borderColor: `hsl(var(${token}) / .3)`,
        }

  const content = (
    <>
      <span aria-hidden="true" className="num shrink-0 text-2xs leading-none">
        {TONE_GLYPH[resolved]}
      </span>
      <span className="truncate">{children}</span>
      {trailing}
    </>
  )

  // La altura no cambia entre variantes: en una tabla, dos celdas vecinas con
  // distinto énfasis tienen que apoyar en la misma línea de base.
  const classes = cn(
    'inline-flex items-center gap-1.5 font-medium whitespace-nowrap',
    variant === 'solid' && 'border rounded-[var(--radius-control)]',
    size === 'sm' ? 'h-5 text-2xs' : 'h-6 text-xs',
    variant === 'solid' && (size === 'sm' ? 'px-1.5' : 'px-2'),
    onClick && 'transition-colors duration-[var(--motion-fast)] hover:brightness-110',
    className,
  )

  if (onClick) {
    return (
      <button type="button" style={style} className={classes} onClick={onClick}>
        {content}
      </button>
    )
  }

  return (
    <span style={style} className={classes}>
      {content}
    </span>
  )
}
